import { Dayjs } from 'dayjs';

export type ChartType = 'line' | 'bar' | 'gauge' | 'stat';

export type SensorGaugeConfig = {
  measurement: string;
  min?:        number;
  max?:        number;
  colour?:     string;
};

export type ElectricityCostConfig = {};

export type PulseConsumptionConfig = {
  channel:    'CNT1' | 'CNT2';
  pulseDiv:  number;
  unit:       string;
};

export type WidgetConfig =
  | SensorGaugeConfig
  | ElectricityCostConfig
  | PulseConsumptionConfig;

export type WidgetInstance = {
  id:             number;
  osm_name:       string;
  widget_type_id: string;
  chart_type:     ChartType;
  title:          string | null;
  colour:         string | null;
  min_value:      number | null;
  max_value:      number | null;
  layout_order:   number;
  measurement:    string | null;
  config:         WidgetConfig | null;
};

export type WidgetComponentProps = {
  osmName:       string;
  dateFromValue: Dayjs | null;
  dateToValue:   Dayjs | null;
  chartType:     ChartType;
  title:         string | null;
  config:        WidgetConfig | null;
  colour?:       string;
  min?:          number;
  max?:          number;
  measurement?:  string;
};

export type WidgetRegistryEntry = {
  id:           string;
  label:        string;
  description:  string;
  chartTypes:   ChartType[];
  configFields: WidgetConfigField[];
};

export type WidgetConfigField = {
  key:          string;
  label:        string;
  type:         'text' | 'number' | 'select' | 'measurement';
  options?:     { value: string; label: string }[];
  default?:     string | number;
  required?:    boolean;
};

export const WIDGET_REGISTRY: WidgetRegistryEntry[] = [
  {
    id:          'sensor_gauge',
    label:       'Gauge',
    description: 'Displays the average value of a single sensor measurement.',
    chartTypes:  ['gauge', 'stat'],
    configFields: [
      { key: 'measurement', label: 'Measurement', type: 'measurement', required: true },
      { key: 'min',         label: 'Min value',   type: 'number', default: 0   },
      { key: 'max',         label: 'Max value',   type: 'number', default: 100 },
    ],
  },
  {
    id:          'sensor_chart',
    label:       'Chart',
    description: 'Plots raw values for any single measurement over time.',
    chartTypes:  ['line', 'bar'],
    configFields: [
      { key: 'measurement', label: 'Measurement', type: 'measurement', required: true },
    ],
  },
  {
    id:          'electricity_cost',
    label:       'Electricity Cost',
    description: 'Calculates running electricity cost using 3-phase current and tariff rates.',
    chartTypes:  ['line', 'bar', 'stat'],
    configFields: [],
  },
  {
    id:          'gas_consumption',
    label:       'Gas Consumption',
    description: 'Calculates gas consumption from pulse counter readings.',
    chartTypes:  ['line', 'bar', 'stat'],
    configFields: [
      { key: 'channel',   label: 'Pulse channel', type: 'select', required: true,
        options: [{ value: 'CNT1', label: 'CNT1' }, { value: 'CNT2', label: 'CNT2' }] },
      { key: 'pulseDiv', label: 'M³ per pulse',  type: 'number', default: 0.01 },
      { key: 'unit',      label: 'Display unit',  type: 'text',   default: 'm³' },
    ],
  },
  {
    id:          'water_consumption',
    label:       'Water Consumption',
    description: 'Calculates water consumption from pulse counter readings.',
    chartTypes:  ['line', 'bar', 'stat'],
    configFields: [
      { key: 'channel',   label: 'Pulse channel', type: 'select', required: true,
        options: [{ value: 'CNT1', label: 'CNT1' }, { value: 'CNT2', label: 'CNT2' }] },
      { key: 'pulseDiv', label: 'Litres per pulse', type: 'number', default: 1.0 },
      { key: 'unit',      label: 'Display unit',     type: 'text',   default: 'L' },
    ],
  },
  {
    id:          'gas_cost',
    label:       'Gas Cost',
    description: 'Calculates gas cost using pulse counter readings and tariff rates.',
    chartTypes:  ['line', 'bar', 'stat'],
    configFields: [
      { key: 'channel',   label: 'Pulse channel', type: 'select', required: true,
        options: [{ value: 'CNT1', label: 'CNT1' }, { value: 'CNT2', label: 'CNT2' }] },
      { key: 'pulseDiv',  label: 'Pulses per m³', type: 'number', default: 100 },
    ],
  },
  {
    id:          'water_cost',
    label:       'Water Cost',
    description: 'Calculates water cost using pulse counter readings and tariff rates.',
    chartTypes:  ['line', 'bar', 'stat'],
    configFields: [
      { key: 'channel',   label: 'Pulse channel', type: 'select', required: true,
        options: [{ value: 'CNT1', label: 'CNT1' }, { value: 'CNT2', label: 'CNT2' }] },
      { key: 'pulseDiv',  label: 'Pulses per m³', type: 'number', default: 100 },
    ],
  },
  {
    id:          'vibration',
    label:       'Vibration',
    description: 'Plots Z/X RMS and peak velocity from vibration sensor.',
    chartTypes:  ['line', 'bar'],
    configFields: [],
  },
  {
    id:          'import_cost',
    label:       'Imported Power Cost',
    description: 'Calculates cumulative electricity cost from imported kWh via main incomer (MODBUS).',
    chartTypes:  ['line', 'bar', 'stat'],
    configFields: [],
  },
  {
    id:          'import_consumption',
    label:       'Imported Power Consumption',
    description: 'Calculates cumulative kWh consumption from imported energy via main incomer (MODBUS).',
    chartTypes:  ['line', 'bar', 'stat'],
    configFields: [],
  },
  {
    id:          'air_quality',
    label:       'Air Quality',
    description: 'Plots PM1, PM4, PM10 and PM2.5 particulate levels.',
    chartTypes:  ['line', 'bar'],
    configFields: [],
  },
  {
    id:          'three_phase_current',
    label:       'Three Phase Current',
    description: 'Plots CC1, CC2 and CC3 phase current readings.',
    chartTypes:  ['line', 'bar'],
    configFields: [],
  },
];

export const getWidgetDef = (id: string): WidgetRegistryEntry | undefined =>
  WIDGET_REGISTRY.find((w) => w.id === id);
